import React, { useCallback } from "react";
import { defineMessages, injectIntl } from "react-intl";
import styled from "styled-components";
import { FormattedMessage } from "components/FormattedMessage";
import { COVID_LINK_CLICK_EVENT_NAME } from "config/metricsEventConfig";
import { TrMetricsService } from "services/TrMetricsService";
import { localeConfig } from "config/localeDriven";
import { AcceptanceButton } from "./SafetyCommitment/AcceptanceButton";

const messages = defineMessages({
  committedToHelping: {
    id: "shared.components.ContactlessModalContents.committedToHelping",
    defaultMessage: "Help keep your community safe!",
  },
  protectTheCommunity: {
    id: "shared.components.ContactlessModalContents.protectTheCommunity",
    defaultMessage:
      "To use TaskRabbit, you agree to follow standards for healthy and safe tasking, shared by everyone in our community: ",
  },
  socialDistancing: {
    id: "shared.components.ContactlessModalContents.socialDistancing",
    defaultMessage:
      "Opt for contactless tasks and keep at least 6 feet/2 meters physical distance from others at all times.",
  },
  inPersonTasks: {
    id: "shared.components.ContactlessModalContents.inPersonTasks",
    defaultMessage:
      "For in-person tasks, confirm you have not had COVID-19, related symptoms, or known exposure to anyone with COVID-19. If you have, be sure to comply with all local health authority quarantine timelines before using TaskRabbit.",
  },
  faceCovering: {
    id: "shared.components.ContactlessModalContents.faceCovering",
    defaultMessage: "Wear a face covering during the task.",
  },
  sanitizeHands: {
    id: "shared.components.ContactlessModalContents.sanitizeHands",
    defaultMessage:
      "Sanitize your hands and all high-touch surfaces and items before, during, and after the task.",
  },
  followHealthGuidelines: {
    id: "shared.components.ContactlessModalContents.followHealthGuidelines",
    defaultMessage:
      "Follow all applicable public health guidelines and regulations.",
  },
  thanksForHealth: {
    id: "shared.components.ContactlessModalContents.thanksForHealth",
    defaultMessage: "Thanks for doing your part to keep your community safe!",
  },
  seeOtherInitiatives: {
    id: "shared.components.ContactlessModalContents.seeOtherInitiatives",
    defaultMessage:
      "See what else we’re doing to protect the TaskRabbit community.",
  },
  covidResourcesTitle: {
    id: "shared.components.ContactlessModalContents.covidResourcesTitle",
    defaultMessage:
      "Additional Resources",
  },
  iAccept: {
    id: "SafetyCommitment.iAccept",
    defaultMessage: "I accept",
  },
});

export function SafetyCommitmentBase({ hideModal, intl: { locale } }) {
  const covidSupportUrl = localeConfig(locale, "support.covid19");
  const covidResourcesLinks = localeConfig(locale, "shared.components.ContactlessModalContents.covidResourcesLinks");

  const onSupportUrlClick = useCallback(() => {
    TrMetricsService.fire(COVID_LINK_CLICK_EVENT_NAME);
    return true;
  }, []);

  const showAcceptanceButton = typeof hideModal === "function";

  return (
    <div>
      <StyledHeader>
        <FormattedMessage message={messages.committedToHelping} />
      </StyledHeader>

      <PrimaryParagraph>
        <FormattedMessage message={messages.protectTheCommunity} />
      </PrimaryParagraph>
      <ul>
        <StyledLi>
          <Row>
            <CheckIcon className="ss-lnr-check" />
            <FormattedMessage message={messages.socialDistancing} />
          </Row>
        </StyledLi>
        <StyledLi>
          <Row>
            <CheckIcon className="ss-lnr-check" />
            <FormattedMessage message={messages.inPersonTasks} />
          </Row>
        </StyledLi>
        <StyledLi>
          <Row>
            <CheckIcon className="ss-lnr-check" />
            <FormattedMessage message={messages.faceCovering} />
          </Row>
        </StyledLi>
        <StyledLi>
          <Row>
            <CheckIcon className="ss-lnr-check" />
            <FormattedMessage message={messages.sanitizeHands} />
          </Row>
        </StyledLi>
        <StyledLi>
          <Row>
            <CheckIcon className="ss-lnr-check" />
            <FormattedMessage message={messages.followHealthGuidelines} />
          </Row>
        </StyledLi>
      </ul>
      {showAcceptanceButton && <AcceptanceButton hideModal={hideModal} />}

      <SecondaryParagraph>
        <FormattedMessage message={messages.thanksForHealth} />
        <br />
        <a
          onClick={onSupportUrlClick}
          href={covidSupportUrl}
          target="_blank"
          rel="noopener"
        >
          <FormattedMessage message={messages.seeOtherInitiatives} />
        </a>
      </SecondaryParagraph>

      <FormattedMessage message={messages.covidResourcesTitle} />
      <br />
      <p dangerouslySetInnerHTML={{ __html: covidResourcesLinks }}/>

    </div>
  );
}

export const SafetyCommitment = injectIntl(SafetyCommitmentBase);

const StyledHeader = styled.h1`
  font-weight: ${(props) => props.theme.typography.weight.semiBold};
  font-size: ${(props) => props.theme.typography.typeScale[500]};
  margin-bottom: ${(props) => props.theme.spacing.lg};
`;

const PrimaryParagraph = styled.p`
  font-size: ${(props) => props.theme.typography.typeScale[200]};
  font-weight: ${(props) => props.theme.typography.weight.regular};
`;

const StyledLi = styled.li`
  margin-bottom: ${(props) => props.theme.spacing.md};
  font-size: ${(props) => props.theme.typography.typeScale[200]};
  font-weight: ${(props) => props.theme.typography.weight.regular};
`;

const Row = styled.div`
  display: flex;
`;

const CheckIcon = styled.i`
  color: ${(props) => props.theme.color.accentIcon};
  font-size: ${(props) => props.theme.typography.typeScale[400]};
  margin-right: ${(props) => props.theme.spacing.md};
`;

const SecondaryParagraph = styled.p`
  font-weight: ${(props) => props.theme.typography.weight.regular};
`;
